import { c as _c } from "react/compiler-runtime";
import { getChatSessionsOptions } from '@onefootprint/fetch-dashboard';
import type { ChatSession } from '@onefootprint/request-types/dashboard';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { peekChatSessionIsLive, rememberChatSessionEnv } from '@/components/agent-chat/utils/chat-session-stash';
import useSession from '@/hooks/use-session';
const PAGE_SIZE = 50;
const parseIsLiveParam = (value: string | string[] | undefined): boolean | undefined => {
  const v = Array.isArray(value) ? value[0] : value;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
};
const useLiveChatSessionsQuery = () => {
  const $ = _c(3);
  const {
    data: session
  } = useSession();
  const isSandboxRestricted = session.org?.isSandboxRestricted;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getChatSessionsOptions({
      query: {
        pageSize: PAGE_SIZE
      },
      headers: {
        "x-is-live": "true"
      }
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = !isSandboxRestricted;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      ...t0,
      enabled: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
};
const useSandboxChatSessionsQuery = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getChatSessionsOptions({
      query: {
        pageSize: PAGE_SIZE
      },
      headers: {
        "x-is-live": "false"
      }
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
};
const useChatSessionsList = () => {
  const $ = _c(6);
  const liveQuery = useLiveChatSessionsQuery();
  const sandboxQuery = useSandboxChatSessionsQuery();
  let t0;
  if ($[0] !== liveQuery.data?.data || $[1] !== sandboxQuery.data?.data) {
    t0 = [...(liveQuery.data?.data ?? []), ...(sandboxQuery.data?.data ?? [])].sort(_temp);
    $[0] = liveQuery.data?.data;
    $[1] = sandboxQuery.data?.data;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const sessions = t0;
  const t1 = sandboxQuery.isLoading || liveQuery.isLoading;
  let t2;
  if ($[3] !== sessions || $[4] !== t1) {
    t2 = {
      sessions,
      isLoading: t1
    };
    $[3] = sessions;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

/**
 * Resolves a chat's env from the cached lists so per-chat fetches can send the
 * matching `x-is-live` header — the detail handlers scope by env and 404 otherwise.
 * Returns `undefined` until both lists settle; then defaults to `true` so a
 * missing id 404s naturally.
 *
 * A shared-link recipient won't find the session in their own lists (shared
 * sessions are owner-scoped in `ChatSession::list`), so the copied link carries
 * the env as `?is_live=`; that takes precedence here to avoid 404ing a sandbox chat.
 */
export const useChatSessionIsLive = chatSessionId => {
  const $ = _c(4);
  const router = useRouter();
  const liveQuery = useLiveChatSessionsQuery();
  const sandboxQuery = useSandboxChatSessionsQuery();
  if (!chatSessionId) {
    return;
  }
  let t0;
  if ($[0] !== router.query.is_live) {
    t0 = parseIsLiveParam(router.query.is_live);
    $[0] = router.query.is_live;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const linkIsLive = t0;
  if (linkIsLive !== undefined) {
    return linkIsLive;
  }
  let t1;
  if ($[2] !== chatSessionId) {
    t1 = peekChatSessionIsLive(chatSessionId);
    $[2] = chatSessionId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const stashedIsLive = t1;
  if (stashedIsLive !== undefined) {
    return stashedIsLive;
  }
  if (liveQuery.data?.data.some(s => s.id === chatSessionId)) {
    rememberChatSessionEnv(chatSessionId, true);
    return true;
  }
  if (sandboxQuery.data?.data.some(s_0 => s_0.id === chatSessionId)) {
    rememberChatSessionEnv(chatSessionId, false);
    return false;
  }
  if (!liveQuery.isLoading && !sandboxQuery.isLoading) {
    return true;
  }
};
export default useChatSessionsList;
function _temp(a, b) {
  return a.createdAt < b.createdAt ? 1 : -1;
}